/**
 * The refuge's facts, as data rather than as prose.
 *
 * These numbers were previously literals scattered across templates and the
 * five locale files: the altitude sat in `pages/index.vue` and again in
 * `components/MeteoCamera.vue` (which disagreed with it), the email and phone
 * in both `app.vue` and `components/ContactInformation.vue`, and the rates only
 * ever as formatted strings like `"€40 / €35*"`.
 *
 * Nothing here is user-facing copy. The locale files still own every visible
 * string; this module owns the machine-readable twin of the same facts, which
 * is what `app.vue` feeds to schema.org and what `public/llms.txt` quotes.
 * `tests/structuredData.test.ts` pins the two together so they cannot drift.
 */

export const refuge = {
    /** Metres above sea level. The camera sits at the refuge, not above it. */
    elevationMetres: 1665,
    establishedYear: 1998,
    /**
     * How many people the refuge sleeps, across the shared sleeping areas.
     * Not a bed count — the arrangement is not one fixed bed per head.
     */
    capacity: 40,

    /**
     * What actually locates the refuge — there is no street address, the road
     * up is unnamed. The same coordinates `server/api/weather.ts` forecasts on.
     */
    geo: {
        latitude: 39.43,
        longitude: 21.17,
    },

    contact: {
        email: 'booking@katarraktismountainrefuge.com',
        /** As displayed. */
        phone: '+30 6972771295',
        /** E.164, for `tel:` hrefs and schema.org `telephone`. */
        phoneE164: '+306972771295',
    },

    social: {
        facebook: 'https://www.facebook.com/KatarraktisRefuge',
        instagram: 'https://www.instagram.com/katarraktis.refuge/',
        youtube: 'https://www.youtube.com/@katarraktisrefuge9897',
    },

    currency: 'EUR',

    /**
     * Euro amounts, unformatted. The visible copy lives in the locale files
     * (`accommodationPrice`, `highlightTentValue`, `sleepCosts`, `tentCost`)
     * and spells the same figures with a currency symbol and a footnote mark.
     */
    rates: {
        /** Per person, per night, half board included. */
        overnight: {
            perNight: 40,
            /** Per night, for stays of two nights or more. */
            multiNight: 35,
        },
        /** Per person, per night, in your own tent. */
        camping: {
            perPerson: 5,
            /** Per person, when the refuge's facilities are used. */
            withFacilities: 10,
        },
    },
} as const;

/**
 * schema.org `priceRange` for the LodgingBusiness — what it costs to sleep in
 * the building, cheapest to dearest.
 *
 * Camping is deliberately outside it. €5 is what you pay to pitch your own
 * tent, and a range starting there reads as "you can stay here from €5", which
 * is only true if you carry a tent up the mountain. The camping rate is
 * published as its own `Offer` instead, which is where a crawler looking for it
 * will find it. `tests/structuredData.test.ts` pins that boundary, because this
 * is exactly the kind of well-meant widening that creeps back.
 */
export const lodgingPriceRange =
    `€${refuge.rates.overnight.multiNight}–€${refuge.rates.overnight.perNight}`;

/**
 * Amenities, each one read off the answer the owner gives in `data/faq.ts` —
 * never assumed. Claiming a facility the refuge does not have is worse than
 * omitting it, so the honest negatives are here too: there is no laundry
 * service, and accessibility is partial rather than general.
 *
 * Names stay English. They are schema.org vocabulary read by crawlers, not
 * copy shown to a visitor, and translating them would mean eight new keys in
 * five files for strings no human ever sees — the same reasoning that leaves
 * `paymentAccepted` and `currenciesAccepted` in English in `app.vue`.
 */
export interface Amenity {
    name: string;
    value: boolean;
    /**
     * Carried into the JSON-LD as the feature's `description`. Set it wherever
     * a bare true would promise more than the refuge offers — the name alone
     * is what a crawler quotes, so the caveat has to travel with it.
     */
    description?: string;
}

export const amenities: readonly Amenity[] = [
    { name: 'Free Wi-Fi', value: true },                    // faqWifiAnswer
    { name: 'Electricity', value: true },                   // faqElectricityAnswer — solar, generator backup
    { name: 'Drinking water from the tap', value: true },   // faqWaterAnswer
    {
        // faqBathroomsAnswer. True, but emphatically not in the sense a hotel
        // means it. There is one shower in the whole building, and water at
        // 1665m is a limited resource carried by the mountain rather than a
        // mains supply. The refuge asks guests to leave it for anyone with a
        // genuine sanitary need; showering is not part of a normal overnight
        // stay and should not be presented as one. A bare `true` here would
        // have an assistant list "hot showers" alongside the Wi-Fi.
        name: 'Hot shower',
        value: true,
        description: 'One shower, reserved for guests with a genuine sanitary need. '
            + 'Water is a limited resource at 1665m, so showering is not part of a '
            + 'normal overnight stay and guests are asked not to plan on it.',
    },
    { name: 'Free parking', value: true },                  // faqParkingAnswer — room for ~20 cars
    { name: 'Meals included', value: true },                // sleepCosts — lunch or dinner, plus breakfast
    {
        // faqAccessibilityAnswer — the balcony, living area and dining room
        // only. Toilets and sleeping areas are up stairs and are not.
        name: 'Wheelchair accessible common areas',
        value: true,
        description: 'The outdoor areas, balcony, living area and dining room are accessible. '
            + 'The toilets and sleeping areas are reached by stairs and are not.',
    },
    { name: 'Laundry service', value: false },              // faqLaundryAnswer — explicitly none
];

/**
 * Dogs are welcome outside but not inside, where the sleeping areas are shared
 * (faqDogsAnswer). Schema.org's `petsAllowed` is a property of the lodging, so
 * the truthful answer for a guest asking "can my dog stay with me" is no; the
 * outdoor welcome is in the FAQ answer, which is itself in the markup.
 */
export const petsAllowed = false;

/** faqSmokingAnswer — permitted outside only. */
export const smokingAllowed = false;

const SUMMER_START_MONTH = 6;   // June
const SUMMER_END_MONTH = 10;    // October
const WINTER_START_MONTH = 11;  // November
const WINTER_END_MONTH = 5;     // May

export interface OpeningHoursSpecification {
    '@type': 'OpeningHoursSpecification';
    dayOfWeek: string[];
    opens: string;
    closes: string;
    validFrom: string;
    validThrough: string;
}

const EVERY_DAY = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
];
const WEEKENDS = ['Saturday', 'Sunday'];

function iso(year: number, month: number, day: number): string {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * The two seasons as schema.org opening hours, dated to the year they next run
 * in so the markup never goes stale — a fixed pair of dates would be wrong from
 * the following January.
 *
 * The month boundaries are an approximation, and deliberately so. The refuge
 * opens "in June" and closes "in October", with the exact dates announced each
 * year (`seasonExplanation`); encoding June 1st can therefore be a few days
 * early. That copy stays on the booking page as the authoritative answer. The
 * question this markup exists to answer is the coarse one an agent actually
 * asks — is it open in March — and on that it is right.
 *
 * UTC throughout: the dates are calendar facts, and reading them in the
 * server's local timezone would make the boundary cases flake in tests.
 */
export function currentSeasonWindows(now: Date = new Date()): OpeningHoursSpecification[] {
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth() + 1;

    // Through October we are in (or ahead of) this year's summer; from
    // November the next one is next year's.
    const summerYear = month <= SUMMER_END_MONTH ? year : year + 1;

    // Winter straddles the new year. Through May we are inside the window that
    // opened last November; from June we are looking ahead to the next one.
    const winterStartYear = month <= WINTER_END_MONTH ? year - 1 : year;

    return [
        {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: EVERY_DAY,
            opens: '00:00',
            closes: '23:59',
            validFrom: iso(summerYear, SUMMER_START_MONTH, 1),
            validThrough: iso(summerYear, SUMMER_END_MONTH, 31),
        },
        {
            // November to May the refuge runs weekends, by arrangement with the
            // manager. There is no schema.org way to say "by arrangement", so
            // the days are stated and the caveat stays in the page copy.
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: WEEKENDS,
            opens: '00:00',
            closes: '23:59',
            validFrom: iso(winterStartYear, WINTER_START_MONTH, 1),
            validThrough: iso(winterStartYear + 1, WINTER_END_MONTH, 31),
        },
    ];
}
